import type { BuyOrder, IncomingProduct, ProductProps } from "../types";
import type {
  SupplyDocumentLineItem,
  SupplyDocumentRow,
} from "./listSupplyDocuments";

function parseApiDate(value: string | null | undefined): Date {
  if (!value) {
    return new Date();
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
}

function productCodeOf(item: ProductProps): string {
  return String(item.erp_code ?? item.erpCode ?? "").trim();
}

function parseNumericAttribute(value: string | number | undefined): number | undefined {
  if (value == null || value === "") {
    return undefined;
  }
  const token = String(value).trim().split(/\s+/)[0]?.replace(",", ".");
  const parsed = Number(token);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function enrichIncomingProduct(
  line: SupplyDocumentLineItem,
  catalog: ProductProps[],
): IncomingProduct {
  const attrs = catalog.find((p) => productCodeOf(p) === line.product_code);
  const bulks = line.bulks > 0 ? line.bulks : 1;
  const pkg = attrs?.units?.find((unit) => unit.code === "BUL");
  const unitsPerBulk =
    line.units_per_bulk ??
    (pkg?.units && pkg.units > 0 ? pkg.units : undefined) ??
    1;
  const moqAttr = attrs?.attributes?.find(
    (attr) => attr.name.toLowerCase() === "moq",
  );
  const portAttr = attrs?.attributes?.find(
    (attr) => attr.name.toLowerCase() === "puerto 1",
  );
  const manufacturerAttr = attrs?.attributes?.find(
    (attr) => attr.name.toLowerCase() === "proveedor",
  );
  const unitCbm = (pkg?.cubic_measure ?? 0) / 1_000_000;
  const unitWeight = pkg?.weigth ?? 0;

  const fobAttr = attrs?.attributes?.find(
    (attr) => attr.name === "Costo FOB x Unidad",
  );
  const parsedAttrFob = parseNumericAttribute(fobAttr?.value);
  const unitFob = line.unit_fob ?? parsedAttrFob ?? 0;

  return {
    product_code: line.product_code,
    description: line.description ?? attrs?.description ?? "",
    bulks,
    moq: moqAttr?.value ?? "",
    port: portAttr?.value ?? "",
    manufacturer: manufacturerAttr?.value ?? "",
    cbm: unitCbm,
    units_per_bulk: unitsPerBulk,
    weigth: unitWeight,
    total_units: line.total_units ?? bulks * unitsPerBulk,
    unit_fob: unitFob,
  };
}

export function mapSupplyDocumentsToBuyOrders(
  documents: SupplyDocumentRow[],
  catalog: ProductProps[],
): BuyOrder[] {
  return documents.map((doc) => ({
    id: doc.id,
    number: doc.document_number || doc.id,
    ata: parseApiDate(doc.expected_receipt_date),
    ataw: parseApiDate(doc.ataw_date ?? doc.expected_receipt_date),
    document_status: doc.document_status,
    document_origin: doc.document_origin,
    incoming_product_list: doc.line_items.map((line) =>
      enrichIncomingProduct(line, catalog),
    ),
  }));
}

export function mergeSessionOrdersWithSupplyDocuments(
  sessionOrders: BuyOrder[],
  documentOrders: BuyOrder[],
): BuyOrder[] {
  if (documentOrders.length === 0) {
    return sessionOrders;
  }
  if (sessionOrders.length === 0) {
    return documentOrders;
  }

  const byNumber = new Map(
    documentOrders.map((order) => [order.number, order]),
  );
  const byId = new Map(documentOrders.map((order) => [String(order.id), order]));

  /** Adopta la identidad persistida (id numérico + estado) del documento. */
  const withPersistedIdentity = (
    sessionOrder: BuyOrder,
    docOrder: BuyOrder | undefined,
  ): BuyOrder => {
    if (!docOrder) {
      return sessionOrder;
    }
    return {
      ...sessionOrder,
      id: docOrder.id ?? sessionOrder.id,
      document_status: docOrder.document_status ?? sessionOrder.document_status,
      document_origin: docOrder.document_origin ?? sessionOrder.document_origin,
    };
  };

  return sessionOrders.map((sessionOrder) => {
    const docOrder =
      byNumber.get(sessionOrder.number) ??
      byId.get(String(sessionOrder.id));

    const hasLines = (sessionOrder.incoming_product_list?.length ?? 0) > 0;
    if (hasLines) {
      // Conserva las líneas de sesión, pero enlaza con el documento persistido
      // para que la OC se reconozca como guardada (habilita descargar PDF, etc.).
      return withPersistedIdentity(sessionOrder, docOrder);
    }
    if (!docOrder || (docOrder.incoming_product_list?.length ?? 0) === 0) {
      return withPersistedIdentity(sessionOrder, docOrder);
    }
    return {
      ...withPersistedIdentity(sessionOrder, docOrder),
      incoming_product_list: docOrder.incoming_product_list,
      ata: docOrder.ata ?? sessionOrder.ata,
      ataw: docOrder.ataw ?? sessionOrder.ataw,
    };
  });
}
